import { useMemo } from "react";
import { useAppSelector } from "../redux";
import type { SessionPermissionAction } from "../utils/permissions/flattenRolePermissions";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS, PARTNER_PROJECT_ACCESS_KEYS } from "../utils/permissions/partnerPortalPermissionKeys";

function allowsAccess(action: SessionPermissionAction | undefined): boolean {
  return action === "edit" || action === "view";
}

export function resolvePartnerProjectsPermissionAction(
  map: Record<string, SessionPermissionAction>
): SessionPermissionAction | undefined {
  const accessProjects = map[PP_KEYS.ACCESS_PROJECTS];
  if (accessProjects !== undefined) return accessProjects;
  return map[PP_KEYS.PARTNER_PROJECT_LIST];
}

export function usePartnerSessionPermissionAction(
  permissionKey: string
): SessionPermissionAction | undefined {
  return useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap[permissionKey]
  );
}

export function usePartnerSessionPermissionsLoading(): boolean {
  return useAppSelector((state) => state.auth.sessionPermissionsLoading);
}

export function usePartnerSessionPermissionsLoaded(): boolean {
  return useAppSelector((state) => state.auth.sessionPermissionsLoaded);
}

/** True when user may open the module (edit or view) on the partner portal matrix. */
export function usePartnerSessionCanAccessModule(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap[permissionKey]
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return action === "edit" || action === "view";
  }, [roleId, loading, loaded, action]);
}

export function usePartnerSessionModuleReadOnly(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const action = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap[permissionKey]
  );

  return !!roleId && loaded && action === "view";
}

export function usePartnerSessionCanEditModule(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap[permissionKey]
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return action === "edit";
  }, [roleId, loading, loaded, action]);
}

export function usePartnerSessionModuleDenied(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap[permissionKey]
  );

  return useMemo(() => {
    if (!roleId || loading || !loaded) return false;
    return action === undefined || action === "na";
  }, [roleId, loading, loaded, action]);
}

/** True if any key grants edit or view (OR). Without role_id, legacy allow. */
export function usePartnerSessionCanAccessAnyModule(
  permissionKeys: readonly string[]
): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPartnerPermissionMap = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return permissionKeys.some((key) =>
      allowsAccess(sessionPartnerPermissionMap[key])
    );
  }, [roleId, loading, loaded, sessionPartnerPermissionMap, permissionKeys]);
}

/** True when every key is missing or NA after session load. */
export function usePartnerSessionAllModulesDenied(
  permissionKeys: readonly string[]
): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPartnerPermissionMap = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap
  );

  return useMemo(() => {
    if (!roleId || loading || !loaded) return false;
    return permissionKeys.every((key) => {
      const action = sessionPartnerPermissionMap[key];
      return action === undefined || action === "na";
    });
  }, [roleId, loading, loaded, sessionPartnerPermissionMap, permissionKeys]);
}

/**
 * Access Projects (roles UI) — prefers `access_projects`, falls back to
 * `partner_project_list` when the new key is absent from the session map.
 */
export function usePartnerSessionCanAccessProjects(): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPartnerPermissionMap = useAppSelector(
    (state) => state.auth.sessionPartnerPermissionMap
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return false;

    return allowsAccess(
      resolvePartnerProjectsPermissionAction(sessionPartnerPermissionMap)
    );
  }, [roleId, loading, loaded, sessionPartnerPermissionMap]);
}

export { PARTNER_PROJECT_ACCESS_KEYS };
